import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { Hidden } from '@material-ui/core';
import Loader from '@cpg-its/ads.common.loading-circle';
import { useHistory } from 'react-router-dom';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { InstitutionState } from './institution-model';
import { useInstitutionSetting } from '../../components/use-institution-settings';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { GetInstitutionRemoveRequestValidationSchema, InstitutionRemoveRequest } from './institution-remove-request-form-model';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { FormField } from '../../../../global/components/form-element/form-field';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { setInstitutionRemoveRequestStatus } from './institution-slice';
import { postRemoveOrganizationRequest } from '../../../../global/ducks/organization-prc-api';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { GetRemoveResponseRedirectUrl, GetResponseStatus } from './managed-institution-helper';
import AdoptingInstitutionView from './plan-adoption/display-components/adopting-institution-view';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';

function InstitutionRemoveRequestApp() {
	const app = useContext(DemographicsContext);
	const history = useHistory();
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const institutionSettings = useInstitutionSetting();

	const dispatch = useDispatch();
	const gpsItem = getGpsType(GpsType.INSTITUTION_REMOVE_REQUEST, app);

	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);


	const validationSchema = GetInstitutionRemoveRequestValidationSchema(labelSettings, institutionSettings);

	const initialInstitution = institutionState?.currentInstitution as any;

	const initialState: InstitutionRemoveRequest = {
		closing_date: null,
		comments: '',
	};



	const apiErrors = useApiResponseError();

	const requestSubmittedPath = useBasePath(GetRemoveResponseRedirectUrl());


	if (!initialInstitution) {
		return (<Loader />);
	}

	if (initialInstitution.is_removed) {
		dispatch((setInstitutionRemoveRequestStatus(ApiStatuses.NoChange)));
		history.push(requestSubmittedPath);
	}

	const onSubmitHandler = (update: InstitutionRemoveRequest) => {
		const OnSuccess = (response) => {
			dispatch((setInstitutionRemoveRequestStatus(GetResponseStatus(response))));
			history.push(requestSubmittedPath, { date: getFormattedDate(update?.closing_date), institution: initialInstitution?.organization_name });
		};
		dispatch(apiErrorClear());
		dispatch((setInstitutionRemoveRequestStatus(ApiStatuses.None)));

		dispatch(postRemoveOrganizationRequest(institutionState?.institutionClientNumber, update, OnSuccess));
	};

	return (
		<PageContainer>
			<MainContent>
				<Formik
					initialValues={initialState}
					onSubmit={(values) => onSubmitHandler(values)}
					validationSchema={validationSchema}
					enableReinitialize
					initialErrors={apiErrors}>
					{(formProps: FormikProps<InstitutionRemoveRequest>) => (
						<Form>
							<FormWrapper
								formikProps={formProps}
								submitText={labelSettings?.submitText}
								cancelText={labelSettings?.cancelButtonText}
								onCancel={() => { history.goBack(); }}
							>
								<PageHeader
									headerText={institutionSettings?.institutionRemoveRequestTitle}
									description={institutionSettings?.institutionRemoveRequestSubtitle} />

								<ContentSpacer>
									<WysiwygWithReactLink content={institutionSettings?.institutionRemoveRequestDescription} />
								</ContentSpacer>
								<Hidden mdUp>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</Hidden>
								<DividerSection removeDivider>
									<AdoptingInstitutionView institution={initialInstitution} clientNumberLabel={institutionSettings?.clientIdLabel} sectionTitle={institutionSettings?.institutionIdLabel} />
									<FormGroup>
										<FormField
											name='closing_date'
											label={institutionSettings.closingDateLabel}
											component='datePicker'
											errors={formProps.errors}
											touched={formProps.touched}
											required
											autoWidth
										/>
										<FormField
											name='comments'
											label={institutionSettings?.commentText}
											component='input'
											multiline
											rows={5}
											errors={formProps.errors}
											touched={formProps.touched}
											required
											tooltip={{
												title: institutionSettings.commentHelperText,
											}}
										/>
									</FormGroup>
								</DividerSection>
							</FormWrapper>
						</Form>
					)}
				</Formik>
			</MainContent>
			<SideBarContent>
				<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
			</SideBarContent>
		</PageContainer>
	);
}

export default InstitutionRemoveRequestApp;